// Typed client for the CSL style catalog + per-workspace favorites.
//
// Tables (Day 1 migration):
//   • csl_styles_catalog (id, title, short_title, category, fields[],
//                         language, parent_id, updated_at) — anon-readable.
//   • csl_style_favorites (workspace_id, style_id, position, created_at).
//
// All queries are workspace-scoped via the standard supabase client;
// RLS handles the auth check.

import { supabase } from "@/integrations/supabase/client";

export type CslCatalogEntry = {
  id: string;
  title: string;
  shortTitle: string | null;
  category: string | null;
  language: string | null;
  parentId: string | null;
  updatedAt: string;
};

export type CslFavorite = {
  styleId: string;
  position: number;
};

type CatalogRow = {
  id: string;
  title: string;
  short_title: string | null;
  category: string | null;
  language: string | null;
  parent_id: string | null;
  updated_at: string;
};

const mapRow = (r: CatalogRow): CslCatalogEntry => ({
  id: r.id,
  title: r.title,
  shortTitle: r.short_title,
  category: r.category,
  language: r.language,
  parentId: r.parent_id,
  updatedAt: r.updated_at,
});

/**
 * Fetch entries from the catalog. `search` is an ILIKE token applied to
 * id + title + short_title; `limit` defaults to 50 (the picker keeps the
 * unfiltered list short and pages by typing more characters).
 */
export const searchCatalog = async (
  search: string,
  opts: { limit?: number } = {},
): Promise<CslCatalogEntry[]> => {
  const limit = opts.limit ?? 50;
  let q = supabase
    .from("csl_styles_catalog")
    .select("id, title, short_title, category, language, parent_id, updated_at")
    .order("title", { ascending: true })
    .limit(limit);

  const trimmed = search.trim();
  if (trimmed) {
    // Escape PostgREST `or` filter commas/parens by URI-encoding only what matters:
    // `%` and `,` are the dangerous chars here. The CSL id charset is [a-z0-9-]
    // so a plain ILIKE pattern is safe; titles can contain commas — we accept
    // that by using `ilike` per-column via three separate filters joined with or.
    const pattern = `%${trimmed.replace(/[%_]/g, "\\$&")}%`;
    q = q.or(
      [
        `id.ilike.${pattern}`,
        `title.ilike.${pattern}`,
        `short_title.ilike.${pattern}`,
      ].join(","),
    );
  }

  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return ((data ?? []) as CatalogRow[]).map(mapRow);
};

/** Fetch one entry by id; returns null if not found. */
export const getCatalogEntry = async (
  id: string,
): Promise<CslCatalogEntry | null> => {
  const { data, error } = await supabase
    .from("csl_styles_catalog")
    .select("id, title, short_title, category, language, parent_id, updated_at")
    .eq("id", id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapRow(data as CatalogRow) : null;
};

/** Fetch entries for an explicit id list (used to label favorites/popular). */
export const getCatalogEntries = async (
  ids: string[],
): Promise<CslCatalogEntry[]> => {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from("csl_styles_catalog")
    .select("id, title, short_title, category, language, parent_id, updated_at")
    .in("id", ids);
  if (error) throw new Error(error.message);
  return ((data ?? []) as CatalogRow[]).map(mapRow);
};

// ---------- Favorites ----------

export const listFavorites = async (
  workspaceId: string,
): Promise<CslFavorite[]> => {
  const { data, error } = await supabase
    .from("csl_style_favorites")
    .select("style_id, position")
    .eq("workspace_id", workspaceId)
    .order("position", { ascending: true });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<{ style_id: string; position: number }>).map(
    (r) => ({ styleId: r.style_id, position: r.position }),
  );
};

export const addFavorite = async (
  workspaceId: string,
  styleId: string,
): Promise<void> => {
  // Find the highest current position so we append.
  const { data: max } = await supabase
    .from("csl_style_favorites")
    .select("position")
    .eq("workspace_id", workspaceId)
    .order("position", { ascending: false })
    .limit(1)
    .maybeSingle();
  const next = ((max as { position?: number } | null)?.position ?? -1) + 1;
  const { error } = await supabase
    .from("csl_style_favorites")
    .upsert(
      { workspace_id: workspaceId, style_id: styleId, position: next },
      { onConflict: "workspace_id,style_id" },
    );
  if (error) throw new Error(error.message);
};

export const removeFavorite = async (
  workspaceId: string,
  styleId: string,
): Promise<void> => {
  const { error } = await supabase
    .from("csl_style_favorites")
    .delete()
    .eq("workspace_id", workspaceId)
    .eq("style_id", styleId);
  if (error) throw new Error(error.message);
};

// ---------- Recent (client-only, per browser) ----------

const RECENT_KEY = (workspaceId: string) => `csl-recent:${workspaceId}`;
const RECENT_MAX = 5;

export const listRecent = (workspaceId: string): string[] => {
  try {
    const raw = localStorage.getItem(RECENT_KEY(workspaceId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
};

export const pushRecent = (workspaceId: string, styleId: string): void => {
  try {
    const cur = listRecent(workspaceId).filter((s) => s !== styleId);
    cur.unshift(styleId);
    localStorage.setItem(
      RECENT_KEY(workspaceId),
      JSON.stringify(cur.slice(0, RECENT_MAX)),
    );
  } catch {
    /* swallow — recents are best-effort */
  }
};
